import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

interface RepeatingDetailGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    spacing: number; // m between repeats
    count: number;
}

/**
 * Repeating Detail (BIM: Annotate > Detail > Repeating Detail):
 * the user draws the base detail component stroke, then the tool extends it
 * as evenly spaced repeats along the drawn direction.
 */
export class RepeatingDetailTool extends BaseTool {
    id = 'DETAILS_REPEATING';
    name = 'Repeating Detail';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onActivate(): void {
        this.spacingMm = this.context?.bimStore.toolProperties.repeatingDetailSpacing ?? 250;
        if (!(this.spacingMm > 0)) this.spacingMm = 250;
        this.notify(i18n.t('tools.notify.repeatingDetailHint', { spacing: Math.round(this.spacingMm) }));
    }

    /** Spacing (mm) from the Options Bar; re-read at placement so a mid-tool
     *  change is honored without re-activating. */
    private spacingMm = 250;
    private get spacing(): number { return this.spacingMm / 1000; }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point, 0.05);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            const created = this.createRepeatingDetail(this.startPoint, snapped);
            // exitToSelect captures the store handle BEFORE deactivating;
            // calling this.context after deactivate() threw a null-ref crash
            // and left the tool stuck in the store.
            if (created) {
                this.exitToSelect();
            } else {
                // Stroke too short: keep sketching a new segment.
                this.startPoint = null;
                this.currentPoint = null;
                this.context.appStore.setToolPreview(null);
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point, 0.05);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.startPoint) {
            this.startPoint = null;
            this.currentPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview() {
        if (!this.context || !this.startPoint || !this.currentPoint) return;
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.startPoint,
            end: this.currentPoint
            // No fixed color: the viewport draws previews with the
            // theme ink (white in dark mode, black in light mode)
        });
    }

    private createRepeatingDetail(start: THREE.Vector3, end: THREE.Vector3): boolean {
        if (!this.context) return false;

        const segment = end.clone().sub(start);
        const length = segment.length();
        if (length < this.spacing) {
            this.notify(i18n.t('tools.notify.repeatingDetailStrokeTooShortFor'), 'warning');
            return false;
        }

        // Number of repeats that fit the drawn stroke
        const count = Math.max(Math.floor(length / this.spacing), 1);

        const geometryData: RepeatingDetailGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            spacing: this.spacing,
            count
        };

        const element = {
            id: uuidv4(),
            category: 'Detail Items',
            type: 'Repeating Detail',
            parameters: {
                'Spacing': `${Math.round(this.spacingMm)} mm`,
                'Repeats': count.toString(),
                'Length': `${length.toFixed(3)} m`
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.repeatingDetailCreated', { count: count }), 'success');
        return true;
    }
}
